import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { SignupShell } from './SignupShell';
import { errorKeyFor, verifySignupToken, type ApiResult } from './signupApi';
import {
  clearSignupSession,
  loadSignupSession,
  saveSignupSession,
  type SignupCheckoutSession,
} from './signupSession';
import { useCheckoutLauncher } from './useCheckoutLauncher';

type VerifyState =
  | { kind: 'verifying' }
  | { kind: 'ready'; session: SignupCheckoutSession }
  | { kind: 'failed'; code: string }
  | { kind: 'missing' };

/**
 * The mail token is single-use. React StrictMode runs the effect twice in
 * development, and the second call would burn an "already used" error right
 * after the first one succeeded — so concurrent callers share one request.
 */
const inFlightVerifications = new Map<string, Promise<ApiResult<SignupCheckoutSession>>>();

function verifyOnce(token: string): Promise<ApiResult<SignupCheckoutSession>> {
  const existing = inFlightVerifications.get(token);
  if (existing) return existing;
  const request = verifySignupToken(token);
  inFlightVerifications.set(token, request);
  void request.then(() => inFlightVerifications.delete(token));
  return request;
}

/**
 * Landing page of the verification mail link (#3017): `/signup/verify?token=`.
 *
 * Consuming the token yields the checkout capability, which is kept out of the
 * URL (memory + sessionStorage) — the mail token is stripped from the address
 * bar as soon as it is spent. Payment starts only on an explicit click.
 */
export default function SignupVerifyPage() {
  const { t } = useTranslation('signup');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<VerifyState>({ kind: 'verifying' });

  useEffect(() => {
    let cancelled = false;

    async function run(): Promise<void> {
      if (!token) {
        const session = loadSignupSession();
        setState(session ? { kind: 'ready', session } : { kind: 'missing' });
        return;
      }
      setState({ kind: 'verifying' });
      const result = await verifyOnce(token);
      if (cancelled) return;
      if (!result.ok) {
        setState({ kind: 'failed', code: result.code });
        return;
      }
      saveSignupSession(result.data);
      setState({ kind: 'ready', session: result.data });
      // The spent token has no further use; do not leave it in history or a reload.
      void navigate('/signup/verify', { replace: true });
    }

    void run();
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  const session = state.kind === 'ready' ? state.session : null;
  const launcher = useCheckoutLauncher(session, (failure) => {
    if (failure.code === 'invalid_capability') {
      clearSignupSession();
      setState({ kind: 'failed', code: failure.code });
    } else if (failure.code === 'signup_not_payable') {
      // Already paid, failed or expired: the status page says which.
      void navigate('/signup/status');
    }
  });

  const startAgain = (
    <Button component={RouterLink} to="/signup" variant="contained" sx={{ mt: 3 }}>
      {t('startAgain')}
    </Button>
  );

  return (
    <SignupShell title={t('verifyTitle')} maxWidth={480}>
      {state.kind === 'verifying' ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }} role="status">
          <CircularProgress size={24} />
          <Typography>{t('verifyWorking')}</Typography>
        </Box>
      ) : null}

      {state.kind === 'failed' ? (
        <>
          <Alert severity="error" role="alert">
            <Typography fontWeight={600}>{t('verifyFailedTitle')}</Typography>
            <Typography variant="body2">{t(errorKeyFor(state.code))}</Typography>
          </Alert>
          {startAgain}
        </>
      ) : null}

      {state.kind === 'missing' ? (
        <>
          <Alert severity="error" role="alert">
            <Typography fontWeight={600}>{t('verifyFailedTitle')}</Typography>
            <Typography variant="body2">{t('verifyMissingBody')}</Typography>
          </Alert>
          {startAgain}
        </>
      ) : null}

      {state.kind === 'ready' ? (
        <>
          <Alert severity="success" role="status" sx={{ mb: 2 }}>
            <Typography fontWeight={600}>{t('verifiedTitle')}</Typography>
            <Typography variant="body2">{t('verifiedBody')}</Typography>
          </Alert>
          {launcher.errorCode ? (
            <Alert severity="error" role="alert" sx={{ mb: 2 }}>
              {t(errorKeyFor(launcher.errorCode))}
            </Alert>
          ) : null}
          <Button
            variant="contained"
            fullWidth
            disabled={launcher.pending || launcher.errorCode === 'checkout_not_configured'}
            onClick={() => void launcher.launch()}
            startIcon={launcher.pending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {launcher.pending
              ? t('redirecting')
              : launcher.errorCode
                ? t('retryPayment')
                : t('continuePayment')}
          </Button>
          {launcher.errorCode ? (
            <Button component={RouterLink} to="/signup/status" fullWidth sx={{ mt: 1 }}>
              {t('checkStatus')}
            </Button>
          ) : null}
        </>
      ) : null}
    </SignupShell>
  );
}
